import { colors } from '@kmns-internal/furikake/tokens'
import { LocationOn, OpenInNew } from '@mui/icons-material'
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Checkbox,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  type Device,
  type PlaceNode,
  PlaceNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { type FC, useMemo, useState } from 'react'
import PlaceGroupSVG from 'src/assets/icons/place_groups.svg?react'
import Loading from 'src/components/loading/Loading'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { getKaminashiIDBasePath } from 'src/config'
import { useQueryLoginUserContext } from 'src/features/tanStackQuery/commonQueries/useQueryLoginUserContext'
import FooterButton from 'src/views/components/drawer/FooterButton'
import { useMutationUpdateDevice } from '../api/useMutationUpdateDevice'
import { useQueryDevicePlaceNodes } from '../api/useQueryDevicePlaceNodes'

// キオスク端末のロールを理解するためのヘルプページ
const DEVICE_ROLE_HELP_URL =
  'https://docs.channel.io/kaminashireport/ja/articles/%E3%83%A6%E3%83%BC%E3%82%B6%E3%83%BC%E3%82%92%E7%90%86%E8%A7%A3%E3%81%99%E3%82%8B-2644287c'

const placeNodeName = (node: PlaceNode): string =>
  (node.type === PlaceNodeTypeEnum.PlaceGroup
    ? node.placeGroup?.name
    : node.place?.name) ?? '名称未設定'

interface OwnProps {
  device: Device
  onClose: () => void
}

const EditDevice: FC<OwnProps> = ({ device, onClose }) => {
  const { data: devicePlaceNodes, isLoading: isLoadingPlaceNodes } =
    useQueryDevicePlaceNodes()
  const placeOptions = devicePlaceNodes?.allNodes ?? []
  const { mutate: updateDevice, isPending } = useMutationUpdateDevice()
  const { showSuccessSnackbar } = useSnackbar()
  const { data: loginUserContext } = useQueryLoginUserContext()

  const linkToDeviceListPage = useMemo(() => {
    const rumpRealmId = loginUserContext?.rumpRealmId
    if (!rumpRealmId) {
      return getKaminashiIDBasePath()
    }
    // 個別のキオスク端末の編集画面への遷移は Rump 側の URL 仕様に依存するため、
    // キオスク端末一覧ページへのリンクに留める
    return new URL(
      `/realms/${rumpRealmId}/devices`,
      getKaminashiIDBasePath(),
    ).toString()
  }, [loginUserContext])

  const [selectedUuids, setSelectedUuids] = useState<string[]>(
    device.placeNodes.map(placeNode => placeNode.uuid),
  )

  const onSubmit = () => {
    updateDevice(
      { deviceId: device.id, body: { placeNodeUuids: selectedUuids } },
      {
        onSuccess: () => {
          showSuccessSnackbar('キオスク端末を更新しました')
          onClose()
        },
      },
    )
  }

  return (
    <Stack sx={{ minHeight: '100vh' }}>
      <Typography variant="h6" sx={{ my: 2, px: 3 }}>
        キオスク端末の編集
      </Typography>
      <Divider />
      <Stack
        justifyContent="space-between"
        sx={{ display: 'flex', height: '100%' }}
      >
        <Stack sx={{ px: 3, py: 2 }} spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {device.name}
          </Typography>

          {/* ロール（役割）: キオスク端末は選択不可で固定表示 */}
          <Stack spacing={1}>
            <Typography variant="subtitle1">ロール（役割）</Typography>
            <Typography variant="body1">キオスク端末</Typography>
            <Typography variant="body2">
              ロールについて詳しくは
              <Link
                sx={{ mx: 0.5 }}
                href={DEVICE_ROLE_HELP_URL}
                target="_blank"
                rel="noreferrer"
              >
                ヘルプページ
                <OpenInNew
                  sx={{
                    display: 'inline-block',
                    ml: 0.5,
                    width: '12px',
                    verticalAlign: 'middle',
                  }}
                />
              </Link>
              をご覧ください。
            </Typography>
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle1">所属する現場</Typography>
            {isLoadingPlaceNodes ? (
              <Loading />
            ) : (
              <Autocomplete<PlaceNode, true, false, false>
                multiple
                disableCloseOnSelect
                options={placeOptions}
                getOptionLabel={placeNodeName}
                value={selectedUuids
                  .map(uuid =>
                    placeOptions.find(option => option.uuid === uuid),
                  )
                  .filter(
                    (option): option is PlaceNode => option !== undefined,
                  )}
                onChange={(_, newValue) => {
                  setSelectedUuids(newValue.map(option => option.uuid))
                }}
                isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      mr={1}
                    >
                      <Checkbox size="small" checked={selected} />
                      {option.type === PlaceNodeTypeEnum.PlaceGroup ? (
                        <PlaceGroupSVG
                          style={{
                            width: '16px',
                            color: colors.action.active,
                          }}
                        />
                      ) : (
                        <LocationOn
                          sx={{ width: '16px', color: colors.action.active }}
                        />
                      )}
                      {placeNodeName(option)}
                    </Stack>
                  </li>
                )}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField {...params} />
                )}
              />
            )}
          </Stack>

          <Typography variant="body2" sx={{ mt: 2 }}>
            その他のキオスク端末情報の編集・キオスク端末の削除は
            <Link
              sx={{ mx: 0.5 }}
              href={linkToDeviceListPage}
              target="_blank"
              rel="noreferrer"
            >
              カミナシ ID管理
              <OpenInNew
                sx={{
                  display: 'inline-block',
                  ml: 0.5,
                  width: '12px',
                  verticalAlign: 'middle',
                }}
              />
            </Link>
            で行ってください。
          </Typography>
        </Stack>

        <Stack sx={{ px: 3, py: 2 }}>
          <FooterButton
            spinning={isPending}
            onCancel={onClose}
            onSubmit={onSubmit}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}

export default EditDevice
